'use client';
import React, { useState } from 'react';
import { useProverbData } from '../hooks/useProverbData';
import ShareButton from '../components/ShareButton';
import DevotionalButtons from '../components/DevotionalButtons';
import { formatText, sanitizeHtml } from '../utils/textFormatting';
import { getDayOfYear } from 'date-fns';

const DevotionalHomePage = ({ currentDate }) => {
  const {
    proverbData,
    loading,
    error
  } = useProverbData(currentDate);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fontSize, setFontSize] = useState('text-lg');

  const handleFontSizeChange = () => {
    setFontSize(current => current === 'text-lg' ? 'text-4xl' : 'text-lg');
  };

  const renderDevotionalContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="animate-pulse space-y-4 w-full max-w-2xl lg:max-w-3xl mx-auto">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
          <p className="text-gray-600 mt-4">Loading your daily devotional...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <div className="bg-red-50 p-4 rounded-lg max-w-2xl lg:max-w-3xl mx-auto">
            <p className="text-red-600 mb-4">Error loading data: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (!proverbData) {
      return (
        <div className="text-center py-8">
          <div className="bg-yellow-50 p-4 rounded-lg max-w-2xl mx-auto">
            <p className="text-yellow-800 mb-2">No devotional available for {currentDate.toLocaleDateString()}.</p>
            <p className="text-yellow-700">Please try another date or check back later.</p>
          </div>
        </div>
      );
    }

    // Create the devotional data object for the buttons
    const devotionalData = {
      day: getDayOfYear(currentDate),
      date: currentDate, // Pass the full date object
      title: proverbData.title,
      devotionalContent: proverbData.devotionalContent,
      prayer: proverbData.prayer,
      dailyChallenge: proverbData.dailyChallenge,
      scriptureReference: proverbData.scriptureReference,
      chapter: proverbData.chapter,
      verse: proverbData.verse
    };

    // Split content into lines and determine if we need the "Read More" button
    const contentLines = proverbData.devotionalContent?.split('\n') || [];
    const initialLines = contentLines.slice(0, 5).join('\n');
    // const remainingLines = contentLines.slice(5).join('\n'); // Unused
    const hasMoreContent = contentLines.length > 5;

    return (
      <div className="w-full space-y-4">
        <div className="p-6">
          <div className="text-center">
            <div className="mb-8">
              <h1 className="text-4xl font-serif mb-4">
                {proverbData.scriptureReference}
              </h1>
              <div className="mb-8">
                <div className="relative">
                  <p className="text-2xl font-serif italic mb-2 text-gray-700">
                    {proverbData.verseText}
                  </p>
                  <p className="text-sm text-gray-600 text-right">
                    — {proverbData.translation || 'NET'} {/* Default to NET if no translation specified */}
                  </p>
                </div>
                <DevotionalButtons
                  devotionalData={{
                    ...devotionalData,
                    scriptureReference: undefined // Remove from buttons to avoid duplication
                  }}
                  onFontSizeChange={handleFontSizeChange}
                />
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-3xl font-bold mb-6 text-center">
                {proverbData.title}
              </h2>
              {proverbData.devotionalContent && (
                <div className={`prose max-w-none ${fontSize} leading-relaxed text-left transition-all duration-300`}>
                  <div
                    className="whitespace-pre-line text-gray-800"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(formatText(isExpanded ? proverbData.devotionalContent : initialLines))
                    }}
                  />
                  {hasMoreContent && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-blue-600 hover:text-blue-800 font-medium mt-2"
                    >
                      {isExpanded ? 'Read Less' : 'Read More'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {proverbData.prayer && (
              <div className="bg-purple-50 rounded-lg shadow-sm w-full mt-8">
                <div className="p-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-serif mb-4 text-purple-900">Prayer</h3>
                    <p className="text-lg italic text-purple-800">
                      {proverbData.prayer}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {proverbData.dailyChallenge && (
              <div className="bg-red-50/40 rounded-lg shadow-sm w-full mt-8">
                <div className="p-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-serif mb-4 text-red-800">Today's Challenge</h3>
                    <p className="text-lg text-red-700">
                      {proverbData.dailyChallenge}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <ShareButton
              title={proverbData.title}
              url={window.location.href}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderDevotionalContent()}
    </div>
  );
};

export default DevotionalHomePage;